import React, { useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import bgImage from "../../assets/images/37.png";
import { registerUser } from "../../features/user/userAPI";
import { ROLE } from "../../constants/constants";
import {
  detailsInitialValues,
  detailsValidationSchema,
} from "../../constants/auth.constants";
import intlTelInput from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.css";

function SignUpDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { email, password } = location.state || {};

  const phoneInputRef = useRef(null);
  const formikRef = useRef(null);
  const itiRef = useRef(null);

  useEffect(() => {
    if (phoneInputRef.current) {
      const iti = intlTelInput(phoneInputRef.current, {
        initialCountry: "us",
        preferredCountries: ["in", "us", "gb"],
        separateDialCode: true,
        utilsScript:
          "https://cdn.jsdelivr.net/npm/intl-tel-input@23.6.0/build/js/utils.js",
      });

      itiRef.current = iti;

      const inputEl = phoneInputRef.current;

      const handlePhoneChange = () => {
        if (formikRef.current) {
          const rawNumber = inputEl.value.trim();

          formikRef.current.setFieldValue("phoneNumber[0]", rawNumber);
          formikRef.current.setFieldTouched("phoneNumber[0]", true, false);
        }
      };

      inputEl.addEventListener("input", handlePhoneChange);
      inputEl.addEventListener("countrychange", handlePhoneChange);

      return () => {
        inputEl.removeEventListener("input", handlePhoneChange);
        inputEl.removeEventListener("countrychange", handlePhoneChange);
        iti.destroy();
      };
    }
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (!email || !password) {
        toast.error("Signup data missing. Please start again.");
        navigate("/setting/sign-up");
        return;
      }

      const iti = itiRef.current;

      const dialCode = iti.getSelectedCountryData()?.dialCode || "";
      const phoneRaw = values.phoneNumber[0]?.trim() || "";
      const fullPhoneNumber = `+${dialCode}${phoneRaw}`;

      const registerResponse = await registerUser({
        name: values.name,
        email,
        password,
        phoneNumber: fullPhoneNumber,
        roles: ROLE.USER,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      if (
        registerResponse?.status === 200 ||
        registerResponse?.status === 201
      ) {
        toast.success("Account created successfully!");
        navigate("/verify", { state: { email } });
      } else {
        toast.error(registerResponse?.data?.message || "Signup failed.");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error.message ||
          "Error while registering user.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="main" id="top">
      <div className="container-fluid bg-body-tertiary dark__bg-gray-1200">
        <div
          className="bg-holder bg-auth-card-overlay"
          style={{ backgroundImage: `url(${bgImage})` }}
        ></div>

        <div className="row flex-center position-relative min-vh-100 g-0 py-5">
          <div className="col-11 col-sm-10 col-xl-8">
            <div className="card border border-translucent auth-card">
              <div className="card-body pe-md-0">
                <div className="row align-items-center gx-0 gy-7">
                  <div className="col mx-auto py-8">
                    <div className="auth-form-box text-center">
                      <a
                        className="d-flex flex-center text-decoration-none mb-4"
                        href="/"
                      >
                        <div className="d-flex align-items-center fw-bolder fs-3 d-inline-block">
                          <img
                            src={
                              localStorage.getItem("phoenixTheme") === "dark"
                                ? "/assets/img/icons/logo.png"
                                : "/assets/img/icons/logo-dark.png"
                            }
                            alt="phoenix"
                            width="150"
                          />
                        </div>
                      </a>

                      <h3 className="text-body-highlight mb-2">
                        Just a step ahead!
                      </h3>
                      <p className="text-body-tertiary mb-4">
                        Please provide your details to continue
                      </p>

                      <Formik
                        innerRef={formikRef}
                        initialValues={{
                          ...detailsInitialValues,
                          phoneNumber: [""],
                        }}
                        validationSchema={detailsValidationSchema}
                        onSubmit={handleSubmit}
                      >
                        {({ isSubmitting }) => (
                          <Form noValidate>
                            <div className="mb-3 text-end">
                              <label className="form-label" htmlFor="name">
                                Name
                              </label>
                              <Field
                                id="name"
                                name="name"
                                type="text"
                                placeholder="Name"
                                className="form-control"
                              />
                              <ErrorMessage
                                name="name"
                                component="div"
                                className="invalid-feedback d-block"
                              />
                            </div>

                            <div className="mb-3 text-end">
                              <label
                                className="form-label"
                                htmlFor="phoneNumber"
                              >
                                Phone Number
                              </label>

                              <input
                                ref={phoneInputRef}
                                id="phoneNumber"
                                name="phoneNumber[0]"
                                type="tel"
                                className="form-control"
                                placeholder="Phone Number"
                              />

                              <ErrorMessage
                                name="phoneNumber[0]"
                                component="div"
                                className="invalid-feedback d-block"
                              />
                            </div>

                            <button
                              className="btn btn-primary d-block w-100 mt-3"
                              type="submit"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? "Saving..." : "Continue"}
                            </button>
                          </Form>
                        )}
                      </Formik>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default SignUpDetails;
